import React, { useEffect, useState, useCallback } from "react";
import "./index.less";
import { weevilTrakGetUsers, weevilTrakRegister, weevilTrakUpdateUser } from "@/util/http/WeevilTrak";
import { IWeevilTrakRegisterResModel } from "@/util/http/WeevilTrak/responseType";
import { Table, Button, Input, Modal, Form, Space, Popconfirm, message, Select, Radio, Divider } from "antd";
import { PlusOutlined, DeleteOutlined,EditFilled,EditOutlined, SearchOutlined, UserOutlined, MailOutlined } from "@ant-design/icons";
import { useDebounce } from "ahooks";
import { useDispatch } from "react-redux";
import { updateCommonErrorMessage, updateCommonSuccessMessage } from "@/redux/actions/weevilTrakAction";
import { useNavigate } from "react-router-dom";

const { Option } = Select;

const WeevilTrakUsers = () => {

    const dispatch = useDispatch();
    const navigate = useNavigate();

    const [loading, setLoading] = useState(false);
    const [users, setUsers] = React.useState<IWeevilTrakRegisterResModel[]>([]);
    const [searchText, setSearchText] = useState<string>("");
    const debouncedSearchText = useDebounce(searchText, { wait: 1500 });

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingUser, setEditingUser] = useState<IWeevilTrakRegisterResModel | null>(null);
    const [form] = Form.useForm();

    const [pageNo, setPageNo] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const pageSize = 8;

    const currentUserEmail = localStorage.getItem("weeviltrak-useremail") || "";

    const weevilTrakGetUsersFunc = useCallback(async (isRefresh = false, search = "") => {
        if (loading || (!hasMore && !isRefresh)) return;

        setLoading(true);
        const currentPage = isRefresh ? 1 : pageNo;
        try {
            const res = await weevilTrakGetUsers({
                pageNo: currentPage,
                pageSize: pageSize,
                searchText: search
            });
            if (res && res.data) {
                const newData = Array.isArray(res.data) ? res.data : [];
                if (isRefresh) {
                    setUsers(newData);
                    setPageNo(2);
                } else {
                    setUsers(prev => [...prev, ...newData]);
                    setPageNo(prev => prev + 1);
                }
                // 如果返回的数据少于 pageSize，说明没有更多数据了
                if (newData.length < pageSize) {
                    setHasMore(false);
                } else {
                    setHasMore(true);
                }
            }
        }
        catch (err) {
            if(err?.status === 401){
                dispatch(updateCommonErrorMessage("Unauthorized. Please log in again."));
                navigate("/weeviltrak/login");
            }
            else{
                dispatch(updateCommonErrorMessage("Failed to fetch users. Please try again."));
            }
        }
        finally {
            setLoading(false);
        }
    }, [loading, hasMore, pageNo, pageSize]);

    useEffect(() => {
        weevilTrakGetUsersFunc(true, debouncedSearchText);
    }, [debouncedSearchText]);

    const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchText(e.target.value);
    };

    const handleScroll = (e: any) => {
        const target = e.target;
        const { scrollTop, scrollHeight, clientHeight } = target;
        // 增加触发灵敏度，距离底部 50px 时触发加载
        if (scrollHeight - scrollTop <= clientHeight + 50) {
            weevilTrakGetUsersFunc(false, debouncedSearchText);
        }
    };

    useEffect(() => {
        const tableBody = document.querySelector(".ant-table-body");
        if (tableBody) {
            tableBody.addEventListener("scroll", handleScroll);
        }
        return () => {
            if (tableBody) {
                tableBody.removeEventListener("scroll", handleScroll);
            }
        };
    }, [users, loading, hasMore, debouncedSearchText]); // 当状态、数据或搜索词改变时重新绑定监听

    const showModal = (record?: IWeevilTrakRegisterResModel) => {
        if (record) {
            setEditingUser(record);
            form.setFieldsValue({
                ...record,
                status: record.status || "Active",
                roles: Array.isArray(record.roles) ? record.roles[0] : (record.roles || "Viewer"),
                userId: record.id
            });
        } else {
            setEditingUser(null);
            form.resetFields();
            form.setFieldsValue({
                status: "Active",
                roles: "Viewer",
                password: "Syngenta123"
            });
        }
        setIsModalOpen(true);
    };

    const handleCancel = () => {
        setIsModalOpen(false);
        setEditingUser(null);
        form.resetFields();
    };

    const handleConfirm = async () => {
        setLoading(true);
        try {
            const values = await form.validateFields();
            console.log("Form values:", values);
            if (editingUser) {
                console.log("Editing user:", editingUser.email, values);
                const res = await weevilTrakUpdateUser({
                    operation: "Update",
                    userId: values?.userId,
                    roles: values?.roles ? [values.roles] : undefined,
                    isActive: values?.status === "Active" ? true : false,
                    firstName: values?.firstName,
                    lastName: values?.lastName,
                });
                if (res && res.data) {
                    // dispatch(updateCommonSuccessMessage(res?.data));
                    dispatch(updateCommonSuccessMessage("User Details Updated Successfully!"));
                }
            } else {
                console.log("Adding user:", values);
                // TODO: 调用 Add User 接口
                const res=await weevilTrakUpdateUser({
                    operation: "Add",
                    firstName:values?.firstName,
                    lastName:values?.lastName,
                    email:values?.email,
                    password:values?.password,
                    roles: values?.roles ? [values.roles] : undefined
                });
                if(res.data){
                    dispatch(updateCommonSuccessMessage("Add user successfully!"));
                    // navigate("/weeviltrak/login");
                }else{
                    dispatch(updateCommonErrorMessage("Add user failed. Please try again."));

                }
            }
            setIsModalOpen(false);
            setEditingUser(null);
            form.resetFields();
            weevilTrakGetUsersFunc(true, searchText);
        } catch (error) {
            dispatch(updateCommonErrorMessage(error?.response?.data || "An error occurred. Please try again."));
        }
        finally{
            setLoading(false);
        }
    };

    const columns = [
        {
            title: "First Name",
            dataIndex: "firstName",
            key: "firstName",
            width: "15%",
            sorter: (a: IWeevilTrakRegisterResModel, b: IWeevilTrakRegisterResModel) => {
                const nameA = a.firstName || "";
                const nameB = b.firstName || "";
                return nameA.localeCompare(nameB);
            },
        },
        {
            title: "Last Name",
            dataIndex: "lastName",
            key: "lastName",
            width: "15%",
            sorter: (a: IWeevilTrakRegisterResModel, b: IWeevilTrakRegisterResModel) => {
                const nameA = a.lastName || "";
                const nameB = b.lastName || "";
                return nameA.localeCompare(nameB);
            },
        },
        {
            title: "Email",
            dataIndex: "email",
            key: "email",
            width: "40%",
            sorter: (a: IWeevilTrakRegisterResModel, b: IWeevilTrakRegisterResModel) => {
                const emailA = a.email || "";
                const emailB = b.email || "";
                return emailA.localeCompare(emailB);
            },
        },
        {
            title: "Role",
            dataIndex: "roles",
            key: "roles",
            width: "10%",
            sorter: (a: IWeevilTrakRegisterResModel, b: IWeevilTrakRegisterResModel) => {
                const roleA = Array.isArray(a.roles) ? a.roles[0] : (a.roles || "");
                const roleB = Array.isArray(b.roles) ? b.roles[0] : (b.roles || "");
                return roleA.localeCompare(roleB);
            },
        },
        {
            title: "Status",
            dataIndex: "status",
            key: "status",
            width: "10%",
            sorter: (a: IWeevilTrakRegisterResModel, b: IWeevilTrakRegisterResModel) => {
                const statusA = a.status || "";
                const statusB = b.status || "";
                return statusA.localeCompare(statusB);
            },
        },
        {
            title: "Action",
            key: "action",
            width: "10%",
            render: (_: any, record: IWeevilTrakRegisterResModel) => (
                <Button
                    type="link"
                    icon={<EditOutlined />}
                    onClick={() => showModal(record)}
                    disabled={record?.email === currentUserEmail} // 禁用编辑当前登录用户
                >
                    Edit
                </Button>
            ),
        },
    ];

    return (
        <div className="weevilTrak-users-container" id="weevilTrak-users-container">
            <div className="weevilTrak-users-header">
                <div className="weevilTrak-users-content-title">User Management</div>
                <div className="weevilTrak-users-actions">
                    <Input
                        placeholder="Search users..."
                        prefix={<SearchOutlined />}
                        value={searchText}
                        onChange={handleSearch}
                        style={{ width: 250, marginRight: 16 }}
                        allowClear
                    />
                    <Button
                        type="primary"
                        className="add-user-button"
                        icon={<PlusOutlined />}
                        onClick={() => showModal()}
                    >
                        Add User
                    </Button>
                </div>
            </div>

            <div className="weevilTrak-users-content">
                <Table
                    columns={columns}
                    dataSource={users}
                    rowKey={(record) => record.email}
                    loading={loading}
                    pagination={false}
                    scroll={{ y: 500 }}
                    onRow={() => ({
                        // Ant Design Table 的 onRow 中 onScroll 无法直接捕获到 table body 的滚动
                    })}
                />
            </div>

            {/* show modal for adding/editing user */}
            <Modal
                title={
                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        {editingUser ? <EditFilled style={{ color: "rgb(89, 185, 72)" }} /> : <PlusOutlined style={{ color: "rgb(89, 185, 72)" }} />}
                        <span>{editingUser ? "Edit User Information" : "Add New User Account"}</span>
                    </div>
                }
                open={isModalOpen}
                onOk={handleConfirm}
                onCancel={handleCancel}
                okText={editingUser ? "Save Changes" : "Create User"}
                cancelText="Cancel"
                centered
                width={550}
                maskClosable={false}
                className="weeviltrak-user-modal-container"
                getContainer={() => document.getElementById("weevilTrak-users-container") || document.body}
                destroyOnClose
                okButtonProps={{
                    className: "weeviltrak-user-modal-ok-btn"
                }}
            >
                <Form
                    form={form}
                    layout="vertical"
                    name="userForm"
                    initialValues={{ status: "Active", roles: "Viewer" }}
                    style={{ marginTop: "20px" }}
                >
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" }}>
                        <Form.Item
                            name="firstName"
                            label="First Name"
                            rules={[{ required: true, message: "Please input first name!" }]}
                        >
                            <Input
                                prefix={<UserOutlined style={{ color: "rgb(89, 185, 72)" }} />}
                                placeholder="Enter first name"
                            />
                        </Form.Item>
                        <Form.Item
                            name="lastName"
                            label="Last Name"
                            rules={[{ required: true, message: "Please input last name!" }]}
                        >
                            <Input
                                prefix={<UserOutlined style={{ color: "rgb(89, 185, 72)" }} />}
                                placeholder="Enter last name"
                            />
                        </Form.Item>
                    </div>

                    <Form.Item name="userId" hidden>
                        <Input />
                    </Form.Item>

                    <Form.Item
                        name="email"
                        label="Email Address"
                        rules={[
                            { required: true, message: "Please input email!" },
                            { type: "email", message: "Please enter a valid email!" }
                        ]}
                    >
                        <Input
                            prefix={<MailOutlined style={{ color: "rgb(89, 185, 72)" }} />}
                            placeholder="example@syngenta.com"
                            disabled={!!editingUser}
                        />
                    </Form.Item>

                    {!editingUser && (
                        <Form.Item
                            name="password"
                            label="Password"
                            rules={[{ required: true, message: "Please input password!" }]}
                        >
                            <Input.Password placeholder="Create a secure password" />
                        </Form.Item>
                    )}

                    <Divider orientation="center" plain style={{ fontSize: "14px", color: "rgba(0,0,0,0.45)" }}>Role & Status</Divider>

                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" }}>
                        <Form.Item
                            name="roles"
                            label="User Role"
                            rules={[{ required: true, message: "Please select a role!" }]}
                        >
                            <Select
                                placeholder="Select a role"
                                getPopupContainer={(trigger) => trigger.parentElement || document.body}
                            >
                                <Option value="Viewer">Viewer</Option>
                                <Option value="Admin">Admin</Option>
                            </Select>
                        </Form.Item>

                        <Form.Item
                            name="status"
                            label="Account Status"
                            rules={[{ required: true, message: "Please select status!" }]}
                        >
                            <Radio.Group buttonStyle="solid" style={{ width: "100%" }} className="weeviltrak-status-container">
                                <Radio.Button value="Active" style={{ width: "50%", textAlign: "center" }}>Active</Radio.Button>
                                <Radio.Button value="Inactive" style={{ width: "50%", textAlign: "center" }}>Inactive</Radio.Button>
                            </Radio.Group>
                        </Form.Item>
                    </div>
                </Form>
            </Modal>
        </div>
    );
};

export default WeevilTrakUsers;